import { getText, Tlt, useAPI } from "@clairejs/react";
import { useEffect, useRef, useState } from "react";

import { CreateSessionBody, CreateSessionResponse, IdentityProvider, RegisterAccountBody } from "../../dto/http/user";

import { SessionApi } from "../api/session";
import { UserApi } from "../api/user";

import { GOOGLE_RECAPTCHA_SITE_KEY } from "../../helpers/constants";

import { useDispatch, useErrorHandler } from "../../hooks/system";

const OTP_LENGTH = 6;

export const useCaptcha = () => {
    const w = window as any;
    const idRef = useRef(0);
    const widgetRef = useRef<number>();

    const execute = () => {
        return new Promise<string>((resolve) => {
            w.grecaptcha.ready(() => {
                try {
                    if (widgetRef.current) {
                        w.grecaptcha.reset(widgetRef.current);
                    }

                    const div = document.createElement("div");
                    div.id = `recaptcha-${idRef.current++}`;
                    document.body.appendChild(div);

                    const id = w.grecaptcha.render(div.id, {
                        sitekey: GOOGLE_RECAPTCHA_SITE_KEY,
                        size: "invisible",
                        callback: (response: string) => {
                            document.body.removeChild(div);
                            widgetRef.current = undefined;
                            resolve(response);
                        },
                    });

                    widgetRef.current = id;
                    w.grecaptcha.execute(id);
                } catch (err) {
                    //-- already render then ignore
                }
            });
        });
    };

    useEffect(() => {
        const script = document.createElement("script");
        script.src = "https://www.google.com/recaptcha/api.js?render=explicit";
        script.async = true;
        script.defer = true;
        document.body.appendChild(script);

        return () => {
            document.body.removeChild(script);
            if (widgetRef.current) {
                w.grecaptcha.reset(widgetRef.current);
            }
        };
    }, []);

    return { execute };
};

export const useSignInSignUp = (args: {
    onSessionCreated?: () => Promise<void>;
    solveCaptcha?: () => Promise<string>;
}) => {
    const sessionApi = useAPI(SessionApi);
    const userApi = useAPI(UserApi);
    const errorHandler = useErrorHandler();

    const dispatch = useDispatch((s) => s.me);

    const [loading, setLoading] = useState(false);
    const [otp, setOtp] = useState("");
    const [provider, setProvider] = useState<IdentityProvider>();
    const [showOtp, setShowOtp] = useState<CreateSessionBody>();
    const [showResetPassword, setShowResetPassword] = useState<CreateSessionBody>();
    const [verifying, setVerifying] = useState(false);
    const [otpError, setOtpError] = useState("");
    const [reseting, setReseting] = useState(false);
    const [reset, setReset] = useState(false);

    const handleSession = async (body: CreateSessionBody, response?: CreateSessionResponse) => {
        if (response?.accessToken) {
            //--- to home screen
            await dispatch({ hasToken: true });
            await args.onSessionCreated?.();
        } else if (response?.invalidCredential) {
            //-- login credentials does not match, suggest reset password
            setShowResetPassword(body);
        } else if (response?.notFound) {
            //-- this account does not exists, call sign up
            await signup(body);
        } else if (response?.mustVerify) {
            //-- show otp input
            setShowOtp(body);
        }
    };

    const signup = async (body: RegisterAccountBody) => {
        try {
            setLoading(true);

            //-- trigger captcha & get response
            const captcha = await args.solveCaptcha?.();

            const response = await userApi.registerUser({
                ...body,
                captcha,
            });

            if (response) {
                await handleSession({ provider: body.provider, data: body.data }, response);
            }
        } finally {
            setLoading(false);
        }
    };

    const login = async (body: CreateSessionBody) => {
        try {
            setProvider(provider);
            setLoading(true);

            const response = await sessionApi.createSession(body);

            await handleSession(body, response);
        } catch (err: any) {
            errorHandler.handle(err);
        } finally {
            setProvider(undefined);
            setLoading(false);
        }
    };

    const performVerify = async () => {
        setOtpError("");

        if (otp.length === OTP_LENGTH && showOtp) {
            //-- verify
            try {
                setVerifying(true);
                await userApi.performVerifyEmail({
                    email: showOtp.data[0] as string,
                    otp,
                });

                //-- login again
                setShowOtp(undefined);

                await login({ provider: showOtp.provider, data: showOtp.data });
            } catch (err) {
                setOtpError(getText(<Tlt template="Incorrect verification code" />));
            } finally {
                setVerifying(false);
            }
        }
    };

    const performReset = async (newPassword: string) => {
        if (!showResetPassword) {
            return;
        }

        try {
            setReseting(true);
            if (!reset) {
                await userApi.requestResetPassword({
                    email: showResetPassword.data[0],
                });

                setReset(true);
            } else {
                await userApi.performResetPassword({
                    email: showResetPassword.data[0],
                    otp,
                    secret: newPassword,
                });

                //-- relogin with new password
                setShowResetPassword(undefined);

                await login({ provider: showResetPassword.provider, data: [showResetPassword.data[0], newPassword] });
            }
        } finally {
            setReseting(false);
        }
    };

    return {
        loading,
        verifying,
        reseting,
        resetEmail: showResetPassword?.data[0],
        signup,
        login,
        performVerify,
        setOtp,
        setShowOtp,
        performReset,
        showResetPassword,
        hideResetPassword: () => setShowResetPassword(undefined),
        showOtp,
        otpError,
        setOtpError,
    };
};
